import React from "react";
import { useTranslation } from "react-i18next";
import useVideoTranscode from "../../hooks/useVideoTranscode";
import { truncateVideoText } from "../../utils/string-helpers";
import SvgIcon from "../SvgIcon";

function VideoFieldLabel({ children, showMissing, className = "" }) {
  return (
    <span
      className={`sub-label Add-Exercise-metadataField-label ${className}`.trim()}
    >
      <span>{children}</span>
      {showMissing ? (
        <SvgIcon
          src="/img/new-icons/chip-warning.svg"
          classNames="Add-Exercise-metadataField-warningIcon"
          noFilterOnLight
        />
      ) : null}
    </span>
  );
}

const VideoUpload = ({
  videoFile,
  videoUrl,
  videoOriginal,
  handleInputChange,
  cleanVideoField,
  onBusyChange,
  form,
  showMissingIndicators = false,
}) => {
  const { t } = useTranslation("coaching-portal");
  const {
    onChange,
    preparing,
    progress,
    tooBig,
    cancel,
    reset,
    transcoded,
    dimensions,
  } = useVideoTranscode({
    onChange: handleInputChange,
  });

  // Let the parent disable submit while compressing, so it can't upload mid-transcode.
  React.useEffect(() => {
    onBusyChange?.(preparing);
  }, [preparing, onBusyChange]);

  const showDisableVideoInputClassUrl = videoUrl ? "disable-video-input" : "";
  const urlLabelClass =
    !!videoFile || videoOriginal ? "disable-video-input" : "";

  const renderUrlLabel = () => {
    const text = t("videoUpload.exerciseUrl");
    if (!showMissingIndicators) {
      return <span className={`sub-label ${urlLabelClass}`}>{text}</span>;
    }
    return (
      <VideoFieldLabel showMissing className={urlLabelClass}>
        {text}
      </VideoFieldLabel>
    );
  };

  const renderUploadLabel = () => {
    const text = (
      <>
        {t("videoUpload.videoUploadLabel")}
        {tooBig ? t("videoUpload.fileTooBig") : null}
      </>
    );
    if (!showMissingIndicators) {
      return (
        <span className={`sub-label ${showDisableVideoInputClassUrl}`}>
          {text}
        </span>
      );
    }
    return (
      <VideoFieldLabel showMissing className={showDisableVideoInputClassUrl}>
        {text}
      </VideoFieldLabel>
    );
  };

  return (
    <div className="form-row">
      <div className="form-row-item">
        {renderUrlLabel()}
        <input
          className="url-input"
          type="text"
          name="videoUrl"
          placeholder={`${
            !!videoFile || videoOriginal
              ? ""
              : t("videoUpload.pasteLinkPlaceholder")
          }`}
          value={videoUrl || ""}
          onChange={handleInputChange}
          disabled={!!videoFile || videoOriginal}
          form={form}
        />
      </div>

      <div className="form-row-item">
        {renderUploadLabel()}
        <label
          className={`video-input${
            videoOriginal || videoFile || preparing ? " uploaded" : ""
          } ${showDisableVideoInputClassUrl}`}
        >
          <div>
            <SvgIcon
              src={
                videoOriginal || videoFile
                  ? "/img/new-icons/Video.svg"
                  : "/img/new-icons/upload-file.svg"
              }
              noFilterOnLight={!(videoOriginal || videoFile)}
            />{" "}
            {preparing
              ? t("videoUpload.preparing", {
                  progress: Math.round(progress * 100),
                })
              : videoOriginal || videoFile
                ? `${
                    truncateVideoText(videoOriginal) ||
                    truncateVideoText(
                      videoFile.split("\\")[videoFile.split("\\").length - 1]
                    )
                  }`
                : t("videoUpload.chooseFile")}
          </div>
          {preparing ? (
            <button
              type="button"
              data-button-type="secondry"
              data-icon-only={true}
              data-icon={true}
              onClick={(e) => {
                e.preventDefault();
                cancel();
              }}
            >
              <SvgIcon
                src="/img/new-icons/red-trash.svg"
                noFilterOnLight={true}
              />
            </button>
          ) : (
            (videoOriginal || videoFile) && (
              <button
                type="button"
                data-button-type="secondry"
                data-icon-only={true}
                data-icon={true}
                onClick={(e) => {
                  e.preventDefault();
                  // Clear the picked file and the transcode outcome, then let the
                  // parent drop any already-saved video.
                  reset();
                  cleanVideoField();
                }}
              >
                <SvgIcon
                  src="/img/new-icons/red-trash.svg"
                  noFilterOnLight={true}
                />
              </button>
            )
          )}
          <input
            className={`text-input file-input-other-form ${showDisableVideoInputClassUrl}`}
            type="file"
            name="videoFile"
            // video/* alone hides .mov/.mkv where the OS mime map is incomplete
            accept="video/*,.mov,.mp4,.m4v,.mkv,.avi,.webm,.3gp"
            disabled={!!videoUrl || preparing}
            onChange={onChange}
            form={form}
          />
        </label>
        <input
          type="hidden"
          name="videoTranscoded"
          value={transcoded == null ? "" : String(transcoded)}
          form={form}
        />
        <input
          type="hidden"
          name="videoWidth"
          value={dimensions?.width ?? ""}
          form={form}
        />
        <input
          type="hidden"
          name="videoHeight"
          value={dimensions?.height ?? ""}
          form={form}
        />
        {preparing && <progress value={progress} max={1} />}
      </div>
    </div>
  );
};

export default VideoUpload;
